import React, { useState, useEffect, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Button,
    Box,
    CircularProgress,
} from "@material-ui/core";
import ImageZoom from 'react-image-zoom-rotate';
import { ArrowBack, ArrowForward, Flag } from "@material-ui/icons";
import axios from 'axios'
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { navPath } from "../../constants";
import { HVHeader } from "../shared/DesignSystem";


const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow);

function ViewProjectAnnotation(props) {
    const { projectId, imageId } = useParams();
    const [image, setImage] = useState('')
    const [annotation, setAnnotation] = useState([])
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [flag, setFlag] = useState(false);
    const [taskList, setTaskList] = useState([]);
    const [projectName, setProjectName] = useState("");

    // Derived state for current ID to handle navigation
    // Actually, best to use URL param as source of truth.
    // But handleNext/Prev updates 'state.id' in original code, which was conceptually local state.
    // If I use params, I should push new history.

    const handleDelete = () => {
        axios
            .post("/api/projects/deleteAnnotation", { projectId: projectId, file: imageId + '.json' })
            .then((res) => {
                props.history.push(navPath(`/project/${projectId}`));
            })
            .catch((e) => {
                enqueueSnackbar(e.response.data, {
                    variant: "error",
                });
            });
    }

    const loadData = useCallback(() => {
        setLoading(true);
        axios
            .post("/api/projects/getSingleAnnotation", { projectId: projectId, id: imageId })
            .then((res) => {
                setImage(res.data.img)
                setAnnotation(res.data.annotation)
                setLoading(false);
                if (res.data.flags && res.data.flags[imageId]) {
                    setFlag(res.data.flags[imageId]);
                } else {
                    setFlag(false);
                }
            })
            .catch((e) => {
                enqueueSnackbar(e?.response?.data || "Error loading annotation", {
                    variant: "error",
                });
                setLoading(false);
            });
    }, [projectId, imageId, enqueueSnackbar]);

    const handleEdit = () => {
        // Navigate to Annotate
        props.history.push(navPath(`/annotate/${projectId}/${imageId}`));
    }

    const handlePrevious = () => {
        if (taskList.length === 0) return;
        const currentIndex = taskList.indexOf(imageId);
        let nextIndex = currentIndex - 1;
        if (nextIndex < 0) {
            nextIndex = taskList.length - 1;
        }
        const nextId = taskList[nextIndex];
        props.history.push(navPath(`/viewAnnotation/${projectId}/${nextId}`));
    };

    const handleNext = () => {
        if (taskList.length === 0) return;
        const currentIndex = taskList.indexOf(imageId);
        let nextIndex = currentIndex + 1;
        if (nextIndex > taskList.length - 1) {
            nextIndex = 0;
        }
        const nextId = taskList[nextIndex];
        props.history.push(navPath(`/viewAnnotation/${projectId}/${nextId}`));
    };

    const handleFlag = () => {
        axios
            .post("/api/projects/flags/flag", {
                projectId: projectId,
                imageId: imageId,
                value: !flag,
            })
            .then((res) => {
                setFlag(res.data[imageId]);
            })
            .catch((e) => {
                enqueueSnackbar('Error', {
                    variant: "error",
                });
            });
    };

    const handleKeyNavigation = useCallback((event) => {
        if (!loading) {
            if (event.keyCode === 37) {
                // Left arrow - Previous
                // Cannot call handlePrevious directly if it depends on taskList which is state?
                // But taskList is in scope. However, closure might be stale if not in dependency.
                // Better to just not bind it here or add dependencies.
                // Or use ref for taskList.
            }
            if (event.keyCode === 39) {
                // Right arrow - Next
            }
        }
    }, [loading]);
    // Wait, handleKeyNavigation needs access to taskList and history. 
    // It's better to implement it inside useEffect or use refs. 
    // Let's rely on button clicks or fix this properly. 
    // Since I'm refactoring, let's keep it simple first. 
    // I will skip keyboard navigation fix for a moment or include it with warning.

    // Actually, I can use useEffect to attach/detach listener and it will re-attach when dependencies change.

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (loading) return;
            if (event.keyCode === 37) {
                // Previous logic
                if (taskList.length === 0) return;
                const currentIndex = taskList.indexOf(imageId);
                let nextIndex = currentIndex - 1;
                if (nextIndex < 0) nextIndex = taskList.length - 1;
                const nextId = taskList[nextIndex];
                props.history.push(navPath(`/viewAnnotation/${projectId}/${nextId}`));
            }
            if (event.keyCode === 39) {
                // Next logic
                if (taskList.length === 0) return;
                const currentIndex = taskList.indexOf(imageId);
                let nextIndex = currentIndex + 1;
                if (nextIndex > taskList.length - 1) nextIndex = 0;
                const nextId = taskList[nextIndex];
                props.history.push(navPath(`/viewAnnotation/${projectId}/${nextId}`));
            }
        };

        document.addEventListener("keydown", handleKeyDown, false);
        return () => {
            document.removeEventListener("keydown", handleKeyDown, false);
        };
    }, [loading, taskList, imageId, projectId, props.history]);

    useEffect(() => {
        if (projectId && imageId) {
            loadData();
        }
    }, [projectId, imageId, loadData]);

    useEffect(() => {
        const fetchTaskList = async () => {
            try {
                const [statusRes, imagesRes] = await Promise.all([
                    axios.post("/api/projects/status", { projectId }),
                    axios.post("/api/projects/images/list", { projectId }),
                ]);
                setProjectName(statusRes.data.name || projectId);
                const images = imagesRes.data.images || [];
                const list = images
                    .filter(obj => obj.annotation)
                    .map(obj => obj.key.split('/')[2].split('.')[0]);
                setTaskList(list);
            } catch (err) {
                console.log("Error fetching task list", err);
            }
        };
        if (projectId) {
            fetchTaskList();
        }
    }, [projectId]);

    const handleGoBack = () => props.history.push(navPath(`/project/${projectId}`));

    return (
        <div style={{ background: '#fafafa', minHeight: '100vh' }}>
            <HVHeader tab="projects" showTabs={false} role={{}} env="STAGING" onLogout={() => props.history.push('/login')} />
            <div style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '14px 40px' }}>
                <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Button variant="outlined" size="small" onClick={handleGoBack} startIcon={<ArrowBack />}>Back</Button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#666' }}>
                        <span>Projects</span>
                        <span>/</span>
                        <span style={{ cursor: 'pointer' }} onClick={handleGoBack}>{projectName || projectId}</span>
                        <span>/</span>
                        <span style={{ color: '#000', fontWeight: 600 }}>Image {imageId}</span>
                    </div>
                </div>
            </div>
            <div style={{ padding: '24px 40px', maxWidth: 1440, margin: '0 auto' }}>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </Box> :
                        <>
                            <ImageZoom
                                rotate={(clockwise) => {
                                    return (
                                        <div>
                                            <Button onClick={clockwise} style={{ position: 'absolute', right: 0, zIndex: 1 }}>
                                                ↻
                                            </Button>
                                        </div>
                                    );
                                }}
                                src={`data:image/png;base64,${image}`}
                                width={650}
                                zoomWidth={500}
                            />
                        </>}
                </Grid>
                <Grid item xs={6}>
                    <TableContainer>
                        <Table aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Labels</StyledTableCell>
                                    <StyledTableCell align="center">Value</StyledTableCell>
                                    <StyledTableCell align="center">Start Index</StyledTableCell>
                                    <StyledTableCell align="center">End Index</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {annotation.length === 0 ? '' : annotation.map((row) => (
                                    <StyledTableRow key={row.name}>
                                        <StyledTableCell component="th" scope="row">
                                            {row.label}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">{row.value}</StyledTableCell>
                                        <StyledTableCell align="center">{row.start}</StyledTableCell>
                                        <StyledTableCell align="center">{row.end}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <br></br>
                    <Button
                        onClick={() => handleEdit()}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        Reannotate
                    </Button>{" "}<Button
                        onClick={() => handleDelete()}
                        variant="contained"
                        color="secondary"
                        disabled={loading}
                    >
                        Delete
                    </Button>{" "}
                    <Button
                        onClick={handlePrevious}
                        variant="contained"
                        color="black"
                        disabled={loading}
                        startIcon={<ArrowBack />}
                    >
                        Previous
                    </Button>{" "}
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        color="black"
                        disabled={loading}
                        endIcon={<ArrowForward />}
                    >
                        Next
                    </Button>{" "}
                    <Button
                        onClick={handleFlag}
                        variant="contained"
                        color="secondary"
                        disabled={loading}
                        endIcon={<Flag />}
                    >
                        {flag ? "Unflag" : "Flag"}
                    </Button>
                    <br></br>
                    <br></br>
                </Grid>
            </Grid>
        </div>
        </div>
    )

}

export default ViewProjectAnnotation